import type {
  RoundData,
  GuessResult,
  LeaderboardEntry,
  LeaderboardResponse,
  RoundStats,
  ExpandedVehicle,
  MySubmission,
  QueueItem,
  AdminSessionListResponse,
} from "./types";

// Same-origin proxy → MO_GAME service binding (see app/api/spot/[...path]).
// mtrobs-game has no public route, so the browser never talks to it directly.
const API = "/api/spot";

// Admin-gated calls go through the validating admin proxy instead (see
// app/api/admin/spot/[...path]): it checks the shared admin token at the web
// edge, then forwards it to the game worker (which validates the same token).
// Lets the relocated /admin/spot pages authenticate with the one shared token.
const ADMIN_API = "/api/admin/spot";

function randomId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}-${Math.random().toString(36).slice(2, 10)}`;
}

function getSessionKey(): string {
  if (typeof window === "undefined") return "";
  let key = localStorage.getItem("spot_session_key");
  if (!key) {
    key = randomId();
    localStorage.setItem("spot_session_key", key);
  }
  return key;
}

function headers(extra?: Record<string, string>): Record<string, string> {
  return {
    "Content-Type": "application/json",
    "X-Session-Key": getSessionKey(),
    ...extra,
  };
}

export async function fetchLatestDate(): Promise<string | null> {
  const res = await fetch(`${API}/round/latest`);
  if (!res.ok) return null;
  const data = await res.json() as { date: string };
  return data.date;
}

export async function fetchRound(date: string, userId?: string): Promise<RoundData | null> {
  const extra: Record<string, string> = {};
  if (userId) extra["X-User-Id"] = userId;
  const res = await fetch(`${API}/round/${date}`, {
    headers: headers(Object.keys(extra).length ? extra : undefined),
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to fetch round: ${res.status}`);
  return res.json();
}

export async function submitGuess(
  roundId: string,
  guess: string,
  userId?: string,
  userName?: string,
): Promise<GuessResult> {
  const extra: Record<string, string> = {};
  if (userId) extra["X-User-Id"] = userId;
  if (userName) extra["X-User-Name"] = userName;
  const res = await fetch(`${API}/round/${roundId}/guess`, {
    method: "POST",
    headers: headers(Object.keys(extra).length ? extra : undefined),
    body: JSON.stringify({ guess }),
  });
  const data = await res.json();
  if (!res.ok)
    return {
      error: data.error || "Guess failed",
      make: { matched: false },
      model: { matched: false },
      gen: { matched: false },
      current_level: -1,
      completed: false,
      score: null,
    };
  return data;
}


export async function fetchRoundStats(roundId: string): Promise<RoundStats> {
  const res = await fetch(`${API}/round/${roundId}/stats`);
  return res.json();
}

export async function fetchLeaderboard(): Promise<LeaderboardResponse> {
  const res = await fetch(`${API}/leaderboard`);
  const data = await res.json() as LeaderboardResponse | LeaderboardEntry[];
  // Tolerate old array shape during rollout
  if (Array.isArray(data)) {
    return {
      min_rounds: 5,
      entries: data.map((e) => ({
        ...e,
        average_score:
          e.rounds_played > 0
            ? Math.round((e.total_score / e.rounds_played) * 10) / 10
            : 0,
      })),
    };
  }
  return data;
}

export async function expandVehicle(
  input: string,
  adminToken: string,
): Promise<ExpandedVehicle> {
  const res = await fetch(`${ADMIN_API}/admin/expand`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Token": adminToken,
    },
    body: JSON.stringify({ input }),
  });
  return res.json();
}

export async function uploadImage(
  formData: FormData,
  adminToken: string,
): Promise<{ image_key: string }> {
  const res = await fetch(`${ADMIN_API}/admin/upload`, {
    method: "POST",
    headers: { "X-Admin-Token": adminToken },
    body: formData,
  });
  return res.json();
}

export async function createRound(
  data: Record<string, unknown>,
  adminToken: string,
): Promise<{ id: string }> {
  const res = await fetch(`${ADMIN_API}/admin/round`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Token": adminToken,
    },
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function fetchQueue(
  adminToken: string,
): Promise<{
  pending_review: QueueItem[];
  queued: QueueItem[];
  today: QueueItem | null;
}> {
  const res = await fetch(`${ADMIN_API}/admin/queue`, {
    headers: { "X-Admin-Token": adminToken },
  });
  // Worker returns { error: 'Unauthorized' } for bad tokens. Surface the
  // status so the caller can show "token rejected" instead of crashing
  // downstream when pending_review/queued come back undefined.
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw Object.assign(
      new Error(`fetchQueue ${res.status}: ${body.slice(0, 200) || res.statusText}`),
      { status: res.status },
    );
  }
  return res.json() as Promise<{
    pending_review: QueueItem[];
    queued: QueueItem[];
    today: QueueItem | null;
  }>;
}

export async function fetchRoundSessions(
  roundId: string,
  adminToken: string,
): Promise<AdminSessionListResponse> {
  const res = await fetch(`${ADMIN_API}/admin/round/${roundId}/sessions`, {
    headers: { "X-Admin-Token": adminToken },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(
      `fetchRoundSessions ${res.status}: ${body.slice(0, 200) || res.statusText}`,
    );
  }
  return res.json() as Promise<AdminSessionListResponse>;
}

export async function overrideSessionScore(
  sessionId: string,
  payload: { make_points: number; model_points: number; gen_points: number },
  adminToken: string,
  userId: string,
): Promise<{ ok: true; score: number }> {
  const res = await fetch(`${ADMIN_API}/admin/session/${sessionId}/score`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Token": adminToken,
      "X-User-Id": userId,
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(
      `overrideSessionScore ${res.status}: ${body.slice(0, 200) || res.statusText}`,
    );
  }
  return res.json() as Promise<{ ok: true; score: number }>;
}

export async function removeFromQueue(
  roundId: string,
  adminToken: string,
  userId: string,
): Promise<void> {
  await fetch(`${ADMIN_API}/admin/queue/${roundId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Token": adminToken,
      "X-User-Id": userId,
    },
    body: JSON.stringify({ action: "remove" }),
  });
}

export async function uploadSubmissionImage(
  formData: FormData,
  userId: string,
): Promise<{ image_key: string }> {
  const res = await fetch(`${API}/submissions/upload`, {
    method: "POST",
    headers: { "X-User-Id": userId },
    body: formData,
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error || "upload failed");
  }
  return res.json();
}

export async function expandSubmission(
  input: string,
  userId: string,
): Promise<ExpandedVehicle> {
  const res = await fetch(`${API}/submissions/expand`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-User-Id": userId },
    body: JSON.stringify({ input }),
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error((data as { error?: string }).error || "expand failed") as Error & {
      status?: number;
    };
    err.status = res.status;
    throw err;
  }
  return data;
}

export async function createSubmission(
  data: Record<string, unknown>,
  userId: string,
): Promise<{ id: string }> {
  const res = await fetch(`${API}/submissions`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-User-Id": userId },
    body: JSON.stringify(data),
  });
  const body = await res.json();
  if (!res.ok) {
    const err = new Error((body as { error?: string }).error || "submit failed") as Error & {
      status?: number;
      body?: unknown;
    };
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body as { id: string };
}

export async function fetchMySubmissions(
  userId: string,
): Promise<{ items: MySubmission[] }> {
  const res = await fetch(`${API}/submissions/mine`, {
    headers: { "X-User-Id": userId },
  });
  if (!res.ok) throw new Error("fetch mine failed");
  return res.json();
}

export async function moveQueueItem(
  roundId: string,
  direction: "up" | "down",
  adminToken: string,
  userId: string,
): Promise<void> {
  const res = await fetch(`${ADMIN_API}/admin/queue/${roundId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Token": adminToken,
      "X-User-Id": userId,
    },
    body: JSON.stringify({ action: "move", direction }),
  });
  if (!res.ok) throw new Error("move failed");
}

export async function approveQueueItem(
  roundId: string,
  adminToken: string,
  userId: string,
  payload?: Record<string, unknown>,
): Promise<void> {
  const res = await fetch(`${ADMIN_API}/admin/queue/${roundId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Token": adminToken,
      "X-User-Id": userId,
    },
    body: JSON.stringify({ action: "approve", ...(payload ? { payload } : {}) }),
  });
  if (!res.ok) throw new Error("approve failed");
}

export async function rejectQueueItem(
  roundId: string,
  adminToken: string,
  userId: string,
  rejectionReason?: string,
): Promise<void> {
  const res = await fetch(`${ADMIN_API}/admin/queue/${roundId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Token": adminToken,
      "X-User-Id": userId,
    },
    body: JSON.stringify({ action: "reject", rejection_reason: rejectionReason }),
  });
  if (!res.ok) throw new Error("reject failed");
}
